/**
 * What a pop-up does once it has popped: whether it is kept in the Pop-ups tab,
 * and where clicking it goes.
 *
 * Policy, not transport — which is why it lives beside the assistant rather than
 * in the API adapter. The adapter's job ends at normalising the DTO; tab names,
 * wizard steps and URLs are this screen's business.
 */
import { meetingStepParam, type MeetingStep } from '@/features/advisory-ai/call/steps'
import type { SettingsTab } from '@/features/advisory-ai/settings/tabs'
import type { PopupEventKey, PopupMessage } from './types'

type PopupRoute =
  | { kind: 'path'; path: string }
  | { kind: 'settings'; tab: SettingsTab }
  /** The meeting the pop-up carries ids for. No step opens it on whatever its status implies. */
  | { kind: 'meeting'; step?: MeetingStep }

type PopupBehaviour = { retain: false } | { retain: true; route: PopupRoute | null }

// Shared by several events each. `as const` so one cannot be mutated out from
// under every event that points at it.
const EPHEMERAL = { retain: false } as const satisfies PopupBehaviour
const KEPT = { retain: true, route: null } as const satisfies PopupBehaviour
const ORG_LIST = { retain: true, route: { kind: 'path', path: '/adviceprep' } } as const satisfies PopupBehaviour
const MEETING_ANALYSIS = {
  retain: true,
  route: { kind: 'meeting', step: 'analysis' },
} as const satisfies PopupBehaviour

/**
 * What each event does once it has popped, as one total table.
 *
 * `Record`, not `Partial`: adding a key to POPUP_EVENT_KEYS will not compile
 * until it is classified here, so a new event cannot ship silently invisible.
 * `retain: false` carries no route at all, because a bubble that is gone the
 * moment it fades has nowhere to be clicked from — an ephemeral-but-clickable
 * entry is unrepresentable rather than merely absent.
 *
 * Retained entries survive in the Pop-ups tab; the rest pop once in the corner
 * and are gone, including the inactivity nudges, which also go out by email.
 */
const POPUP_BEHAVIOUR: Record<PopupEventKey, PopupBehaviour> = {
  // firsttime
  signup: KEPT,
  xero_connected: ORG_LIST,
  first_meeting_created: EPHEMERAL,
  first_agenda_created: EPHEMERAL,
  first_analysis_generated: EPHEMERAL,
  first_notes_added: EPHEMERAL,
  branding_updated: { retain: true, route: { kind: 'settings', tab: 'theme' } },
  avatar_setup: { retain: true, route: { kind: 'settings', tab: 'avatarVoice' } },
  first_export: EPHEMERAL,
  first_meeting_completed: { retain: true, route: { kind: 'meeting' } },
  first_recommendation_edited: EPHEMERAL,
  // everyuse
  login_welcome: EPHEMERAL,
  xero_connected_again: ORG_LIST,
  xero_sync_failed: { retain: true, route: { kind: 'path', path: '/platform' } },
  notes_added: EPHEMERAL,
  analysis_generated: MEETING_ANALYSIS,
  meeting_first_export: EPHEMERAL,
  meeting_tomorrow: { retain: true, route: { kind: 'meeting', step: 'agenda' } },
  // inactivity
  inactive_7d: EPHEMERAL,
  inactive_30d: EPHEMERAL,
  // milestones
  firm_analysis_3: MEETING_ANALYSIS,
  firm_analysis_10: MEETING_ANALYSIS,
  firm_analysis_25: MEETING_ANALYSIS,
  firm_meetings_3: KEPT,
  teammate_first_analysis: KEPT,
  firm_second_meeting: KEPT,
  // Kept deliberately: an event this build has never heard of is better shown
  // unstyled than lost silently.
  unknown: KEPT,
}

export function isRetainedPopup(popup: Pick<PopupMessage, 'eventKey'>): boolean {
  return POPUP_BEHAVIOUR[popup.eventKey].retain
}

/**
 * Where clicking a pop-up goes. Routes are a client concern — the server sends
 * a kind and ids, never a URL — so the mapping lives here beside the adapter.
 *
 * Driven by `eventKey`, not `target.kind`: the kind is too coarse to place half
 * of these. Both settings events arrive as a bare `settings`, and the server
 * sends full `call` targets for events that must not be clickable at all. The
 * target is read only for the ids it carries.
 *
 * Returns null when there is nowhere sensible to go, which the UI reads as
 * "this entry is not clickable".
 */
export function popupTargetRoute(popup: Pick<PopupMessage, 'eventKey' | 'target'>): string | null {
  const behaviour = POPUP_BEHAVIOUR[popup.eventKey]
  if (!behaviour.retain || !behaviour.route) return null

  const route = behaviour.route
  if (route.kind === 'path') return route.path
  if (route.kind === 'settings') return `/adviceprep/settings?tab=${route.tab}`

  const { orgId, callId } = popup.target
  if (!orgId || !callId) return null
  const base = `/adviceprep/org/${orgId}/report/${callId}`
  return route.step ? `${base}?step=${meetingStepParam(route.step)}` : base
}
